import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { AuditLogPage } from "@/features/audit/components/AuditLogPage";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { directoryQuery } from "@/features/directory/queries";
import { masterTypeIndexQuery } from "@/features/master-types/queries";

/**
 * Master-data and schema audit, one page with a domain toggle — DEE-35
 * gates both behind the same `audit.read.master_data` permission
 * (`_REVIEWER` already holds both approve grants), so there is nothing to
 * segregate between them the way master-data and user-management need to
 * be. `AuditLogPage` does the actual work; this file only wires up the
 * loader and which domains it offers.
 */
export const Route = createFileRoute("/_authed/audit/master-data")({
  staticData: { crumbs: [{ key: "nav.audit.masterData" }] },
  // `?entityType=&entityId=`, so a record's own "view audit trail" link
  // lands here pre-filtered instead of making the reader redo it. Mirrors
  // `submissions.index.tsx`'s `?kind=` the same way.
  validateSearch: (search: Record<string, unknown>): { entityType?: string; entityId?: string } => {
    const entityType = typeof search.entityType === "string" ? search.entityType : undefined;
    const entityId = typeof search.entityId === "string" ? search.entityId : undefined;
    return { ...(entityType ? { entityType } : {}), ...(entityId ? { entityId } : {}) };
  },
  loader: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    // The log itself is an infinite query and not prefetched — same
    // reasoning as `submissions.index.tsx`. Only the lookups every row
    // needs are primed here.
    await Promise.all([
      context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale)),
      context.queryClient.ensureQueryData(directoryQuery()),
    ]);
  },
  component: MasterDataAudit,
});

function MasterDataAudit() {
  const { t } = useTranslation();
  const { me } = useSession();
  const search = Route.useSearch();
  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(me.resolved_locale));
  const { data: directory } = useSuspenseQuery(directoryQuery());

  return (
    <AuditLogPage
      title={t("audit.masterData.title")}
      description={t("audit.masterData.description")}
      domainOptions={[
        { value: "master_data", label: t("audit.domain.masterData") },
        { value: "schema", label: t("audit.domain.schema") },
      ]}
      emptyTitle={t("audit.empty.title")}
      emptyBody={t("audit.empty.body")}
      directory={directory}
      masterTypeIndex={index}
      initialFilters={{ entityType: search.entityType, entityId: search.entityId }}
    />
  );
}
